/**
 * Where a removal cuts when its words carry forced-alignment cut points.
 * Times are seconds on the media clock.
 */

export interface CutPointWord {
  media_ts: number;
  media_end_ts: number;
  media_cut_ts?: number;
  media_cut_end_ts?: number;
}

export interface MediaStretch {
  start: number;
  end: number;
}

export type RemovalOperation = 'excise' | 'trimStart' | 'trimEnd';

export type RemovalCut =
  | { kind: 'stretch'; stretch: MediaStretch }
  /** Disjoint, in media order; empty when the removal takes no audio. */
  | { kind: 'pieces'; pieces: MediaStretch[] };

export interface ElementPart extends MediaStretch {
  removed: boolean;
}

/** Seconds; closer times count as equal. */
const EPSILON = 1e-4;

const isTime = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isPlaced = (word: CutPointWord | undefined): word is CutPointWord =>
  !!word && isTime(word.media_ts) && isTime(word.media_end_ts);

const middleOf = (word: CutPointWord) =>
  (word.media_ts + word.media_end_ts) / 2;

const stretchOf = (word: CutPointWord): MediaStretch => ({
  start: word.media_ts,
  end: word.media_end_ts,
});

const overlaps = (a: MediaStretch, b: MediaStretch) =>
  Math.min(a.end, b.end) - Math.max(a.start, b.start) > EPSILON;

const inOrder = (left: MediaStretch, right: MediaStretch) =>
  right.start >= left.start - EPSILON && !overlaps(left, right);

const subtract = (
  stretch: MediaStretch,
  holes: readonly MediaStretch[],
): MediaStretch[] => {
  const out: MediaStretch[] = [];
  let at = stretch.start;
  for (const hole of [...holes].sort((a, b) => a.start - b.start)) {
    if (hole.start > at) {
      out.push({ start: at, end: Math.min(hole.start, stretch.end) });
    }
    at = Math.max(at, hole.end);
    if (at >= stretch.end) break;
  }
  if (at < stretch.end) out.push({ start: at, end: stretch.end });
  return out.filter(piece => piece.end - piece.start > EPSILON);
};

const clampTo = (time: number, element: MediaStretch) =>
  Math.min(Math.max(time, element.start), element.end);

const normalise = (
  pieces: readonly MediaStretch[],
  element: MediaStretch,
): MediaStretch[] => {
  const merged: MediaStretch[] = [];
  const clamped = pieces
    .map(piece => ({
      start: clampTo(piece.start, element),
      end: clampTo(piece.end, element),
    }))
    .filter(piece => piece.end - piece.start > EPSILON)
    .sort((a, b) => a.start - b.start);
  for (const piece of clamped) {
    const last = merged.at(-1);
    if (last && piece.start <= last.end + EPSILON) {
      last.end = Math.max(last.end, piece.end);
    } else {
      merged.push({ ...piece });
    }
  }
  return merged;
};

export const hasCutPoints = (word: CutPointWord): boolean =>
  isTime(word.media_cut_ts) || isTime(word.media_cut_end_ts);

/** Cut for removing `words[from..to]` inclusive; null without cut points. */
export function planRemovalCut({
  words,
  from,
  to,
  operation,
  element,
}: {
  words: readonly CutPointWord[];
  from: number;
  to: number;
  operation: RemovalOperation;
  element: MediaStretch;
}): RemovalCut | null {
  const isRemoved = (index: number) => index >= from && index <= to;
  const removed: number[] = [];
  for (let index = from; index <= to; index++) {
    if (isPlaced(words[index])) removed.push(index);
  }
  if (!removed.some(index => hasCutPoints(words[index]))) return null;

  // Next to a kept word without times, a cut point could cut into its audio.
  const keptUnplaced = (index: number) =>
    index >= 0 &&
    index < words.length &&
    !isRemoved(index) &&
    !isPlaced(words[index]);
  const ownStart = keptUnplaced(from - 1);
  const ownEnd = keptUnplaced(to + 1);

  // A cut point past a kept neighbour's middle is stale (word edited later).
  const cutStart = (index: number): number | undefined => {
    const word = words[index];
    if (!isTime(word.media_cut_ts)) return undefined;
    const point = Math.min(word.media_cut_ts, word.media_ts);
    const previous = words[index - 1];
    if (
      isPlaced(previous) &&
      !isRemoved(index - 1) &&
      point < middleOf(previous) - EPSILON
    ) {
      return undefined;
    }
    return point;
  };
  const cutEnd = (index: number): number | undefined => {
    const word = words[index];
    if (!isTime(word.media_cut_end_ts)) return undefined;
    const point = Math.max(word.media_cut_end_ts, word.media_end_ts);
    const next = words[index + 1];
    if (
      isPlaced(next) &&
      !isRemoved(index + 1) &&
      point > middleOf(next) + EPSILON
    ) {
      return undefined;
    }
    return point;
  };

  const first = removed[0];
  const last = removed[removed.length - 1];
  let cutFrom =
    (ownStart ? undefined : cutStart(first)) ?? words[first].media_ts;
  let cutTo = (ownEnd ? undefined : cutEnd(last)) ?? words[last].media_end_ts;
  // Removed words may be out of media order; take the widest span.
  for (const index of removed.slice(1)) {
    cutFrom = Math.min(cutFrom, cutStart(index) ?? words[index].media_ts);
  }
  for (const index of removed.slice(0, -1)) {
    cutTo = Math.max(cutTo, cutEnd(index) ?? words[index].media_end_ts);
  }

  let nextKept: number | undefined;
  for (let index = to + 1; index < words.length; index++) {
    if (isPlaced(words[index])) {
      nextKept = index;
      break;
    }
  }
  const trimStartEnd =
    nextKept !== undefined
      ? words[nextKept].media_ts
      : words[last].media_end_ts;

  const held = keptWordsHold({
    words,
    removed,
    isRemoved,
    ranges: removed.map((index, k) => ({
      start:
        (k === 0 && ownStart ? undefined : cutStart(index)) ??
        words[index].media_ts,
      end:
        (k === removed.length - 1 && ownEnd ? undefined : cutEnd(index)) ??
        words[index].media_end_ts,
    })),
    operation,
    region:
      operation === 'trimStart'
        ? { start: element.start, end: trimStartEnd }
        : { start: cutFrom, end: element.end },
  });
  if (held) return { kind: 'pieces', pieces: normalise(held, element) };

  const stretch =
    operation === 'trimStart'
      ? { start: element.start, end: trimStartEnd }
      : operation === 'trimEnd'
        ? { start: cutFrom, end: element.end }
        : { start: cutFrom, end: Math.max(cutFrom, cutTo) };
  return {
    kind: 'stretch',
    stretch: {
      start: clampTo(stretch.start, element),
      end: clampTo(stretch.end, element),
    },
  };
}

/**
 * The removal minus the audio of kept words in its way; null if none is.
 * An in-order neighbour is not protected: a cut point may reach into it.
 */
function keptWordsHold({
  words,
  removed,
  isRemoved,
  ranges,
  operation,
  region,
}: {
  words: readonly CutPointWord[];
  removed: readonly number[];
  isRemoved: (index: number) => boolean;
  ranges: readonly MediaStretch[];
  operation: RemovalOperation;
  region: MediaStretch;
}): MediaStretch[] | null {
  const placed: number[] = [];
  words.forEach((word, index) => {
    if (isPlaced(word)) placed.push(index);
  });
  const rank = new Array<number>(words.length).fill(-1);
  placed.forEach((index, k) => {
    rank[index] = k;
  });
  const ordinary = placed
    .slice(1)
    .map((index, k) =>
      inOrder(stretchOf(words[placed[k]]), stretchOf(words[index])),
    );
  const ordinaryNeighbours = (a: number, b: number) =>
    Math.abs(a - b) === 1 && ordinary[Math.min(a, b)];
  const kept = placed
    .filter(index => !isRemoved(index))
    .map(index => ({
      rank: rank[index],
      stretch: stretchOf(words[index]),
    }));
  const ranks = removed.map(index => rank[index]);

  let guarded = false;
  const pieces = ranges.flatMap((range, k) => {
    const holes = kept
      .filter(
        item =>
          !ordinaryNeighbours(ranks[k], item.rank) &&
          overlaps(item.stretch, range),
      )
      .map(item => item.stretch);
    if (holes.length) guarded = true;
    return subtract(range, holes);
  });
  // Out-of-order removed words cut separately, so a kept word between stays.
  const outOfOrder = removed
    .slice(1)
    .some(
      (index, k) =>
        !inOrder(stretchOf(words[removed[k]]), stretchOf(words[index])),
    );

  if (operation === 'excise') return guarded || outOfOrder ? pieces : null;

  const exempt = (itemRank: number) =>
    ordinaryNeighbours(ranks[0], itemRank) ||
    ordinaryNeighbours(ranks[ranks.length - 1], itemRank);
  const inRegion = kept.filter(item => overlaps(item.stretch, region));
  const regionGuarded = inRegion.some(item => !exempt(item.rank));
  if (operation === 'trimStart') {
    return regionGuarded
      ? subtract(
          region,
          inRegion.filter(item => !exempt(item.rank)).map(item => item.stretch),
        )
      : null;
  }
  return regionGuarded || guarded || outOfOrder
    ? [
        ...subtract(
          region,
          inRegion.map(item => item.stretch),
        ),
        ...pieces,
      ]
    : null;
}

export function partsOf(
  pieces: readonly MediaStretch[],
  element: MediaStretch,
): ElementPart[] {
  const parts: ElementPart[] = [];
  let at = element.start;
  for (const piece of pieces) {
    if (piece.start - at > EPSILON) {
      parts.push({ start: at, end: piece.start, removed: false });
    }
    parts.push({ start: piece.start, end: piece.end, removed: true });
    at = piece.end;
  }
  if (element.end - at > EPSILON) {
    parts.push({ start: at, end: element.end, removed: false });
  }
  return parts;
}

/**
 * Part index per word, keeping transcript order, nearest each word's middle.
 * Splitting by time alone can misplace overlapped words; null if none fits.
 */
export function assignWordsToParts(
  words: readonly { middle: number | null; removed: boolean }[],
  parts: readonly ElementPart[],
): number[] | null {
  if (!words.length) return [];
  if (!parts.length) return null;
  const distance = (middle: number | null, part: ElementPart) =>
    middle == null ? 0 : Math.max(0, part.start - middle, middle - part.end);

  // cost[i][j]: the least total distance for words 0..i with word i in part j.
  const cost: number[][] = [];
  const from: number[][] = [];
  words.forEach((word, i) => {
    cost.push(new Array(parts.length).fill(Infinity));
    from.push(new Array(parts.length).fill(-1));
    let bestBefore = i === 0 ? 0 : Infinity;
    let bestBeforeAt = -1;
    parts.forEach((part, j) => {
      if (i > 0 && cost[i - 1][j] < bestBefore) {
        bestBefore = cost[i - 1][j];
        bestBeforeAt = j;
      }
      if (part.removed !== word.removed || bestBefore === Infinity) return;
      cost[i][j] = bestBefore + distance(word.middle, part);
      from[i][j] = bestBeforeAt;
    });
  });

  const lastRow = cost[words.length - 1];
  let at = 0;
  lastRow.forEach((value, j) => {
    if (value < lastRow[at]) at = j;
  });
  if (lastRow[at] === Infinity) return null;
  const assignment = new Array<number>(words.length);
  for (let i = words.length - 1; i >= 0; i--) {
    assignment[i] = at;
    at = from[i][at];
  }
  return assignment;
}

/** First token of each part; punctuation stays with the text before it. */
export function partBoundaries(
  textPositions: readonly number[],
  assignment: readonly number[],
  partCount: number,
  tokenCount: number,
): number[] {
  const boundaries = new Array<number>(partCount + 1).fill(tokenCount);
  boundaries[0] = 0;
  for (let part = 1; part < partCount; part++) {
    const k = assignment.findIndex(assigned => assigned >= part);
    if (k >= 0) boundaries[part] = textPositions[k];
  }
  return boundaries;
}
